import { BUILD_CATALOG, catalogItem, purchaseCost } from '../config/catalog';
import { FLOOR_PAINT_COST, PACING, expansionCost } from '../config/balancing';
import { discountedIntegerCost } from '../config/rewardedAds';
import {
  cycleLocale,
  entityName,
  formatHudGoal,
  getLocale,
  initLocale,
  localeShort,
  paletteLabel,
  parcelName,
  t,
} from '../i18n';
import { entityPanelIncomeView } from '../production/explicitCollect';
import { computeParkScore, upgradeCost } from '../sim/economy';
import { FLOOR_STYLE_IDS } from '../sim/floors';
import {
  isKioskKind,
  kioskCapUiKind,
  kioskCapacity,
  kioskCount,
} from '../sim/kioskCapacity';
import { incomeReserveBlockReason } from '../sim/startSafety';
import { starterGateActive } from '../sim/starterGate';
import { productionFootprint } from '../sim/placement';
import { plotPurchaseGuidance } from '../sim/landGuidance';
import { catalogNeedsLandGuidance, evaluateCatalogUnlock, evaluateParcelUnlock } from '../sim/unlocks';
import {
  CAROUSEL_PALETTES,
  COASTER_PALETTES,
  isCarouselPaletteId,
  isCoasterPaletteId,
} from '../spikes/threejs/palettes';
import {
  guidedCatalogKind,
  recommendedGuideAnchor,
  recommendedGuideSlotId,
  resolveOnboardingStep,
  type OnboardingStep,
} from '../onboarding/onboarding';
import type {
  ActionFailReason,
  ActionResult,
  EntityKind,
  FloorStyleId,
  ParkState,
  PlacedEntity,
  RotationQuarter,
  SubcellAnchor,
} from '../types';
import { shouldExposeDeveloperReset } from './debugControls';
import { hudIconMarkup, type HudIconId } from './icons';
import {
  clampHudPanelDragOffset,
  hudPanelDragEnabled,
  placeHudPanel,
  type PanelPoint,
} from './panelPlacement';

export function catalogFootprintCue(kind: EntityKind): string {
  const spec = productionFootprint(kind);
  return `${spec.cellsX}×${spec.cellsZ}`;
}

export interface RewardedCtaOptions {
  visible: boolean;
  cooldownMs?: number;
  status?: string;
  onWatch?: () => void;
}

export interface EntityPanelHandlers {
  onCollect: () => void;
  onUpgrade: () => void;
  palettes?: Array<{ id: string; label: string }>;
  selectedPaletteId?: string | null;
  onPalette?: (paletteId: string) => void;
  onRequestRemove?: () => void;
  pendingRemove?: boolean;
  onConfirmRemove?: () => void;
  onCancelRemove?: () => void;
  removeRefund?: number;
  onMove?: () => void;
  onRotate?: () => void;
  relocating?: boolean;
  onCancelMove?: () => void;
}

export const HUD_GHOST_CLICK_MS = 400;

/** Stable action vocabulary for the icon-first HUD. Gameplay contracts stay on these ids. */
export const HUD_ACTION_VOCABULARY = [
  'build',
  'land',
  'floor',
  'collect',
  'upgrade',
  'move',
  'rotate',
  'remove',
  'sound',
] as const;

export function hudCommandAllowed(nowMs: number, suppressUntilMs: number): boolean {
  return nowMs >= suppressUntilMs;
}

const GUIDED_CONFIRM_KINDS = ['starter_ride', 'snack_kiosk', 'drink_kiosk'] as const;

export type GuidedConfirmResult =
  | { ok: true }
  | { ok: false; reason: ActionFailReason | 'not_guided' | 'missing_anchor' };

type GuidedConfirmSession = {
  state: ParkState;
  place: (
    slotId: string,
    kind: EntityKind,
    options?: { anchor?: SubcellAnchor; rotation?: RotationQuarter },
  ) => ActionResult;
};

function guidedStepKind(state: ParkState): EntityKind | null {
  const kind = guidedCatalogKind(resolveOnboardingStep(state, { guidedCollectDone: false }));
  if (!kind || !(GUIDED_CONFIRM_KINDS as readonly string[]).includes(kind)) {
    return null;
  }
  return kind;
}

export type GuidedConfirmPlan =
  | { ok: true; slotId: string; kind: EntityKind; anchor: SubcellAnchor }
  | { ok: false; reason: 'not_guided' | 'missing_anchor' };

/** Anchor and slot for the current guided building. Does not place and does not read a world pick. */
export function guidedConfirmPlan(state: ParkState, requestedKind?: EntityKind): GuidedConfirmPlan {
  const kind = guidedStepKind(state);
  if (!kind || (requestedKind !== undefined && requestedKind !== kind)) {
    return { ok: false, reason: 'not_guided' };
  }
  const anchor = recommendedGuideAnchor(state, kind);
  const slotId = recommendedGuideSlotId(state, kind);
  if (!anchor || !slotId) {
    return { ok: false, reason: 'missing_anchor' };
  }
  return { ok: true, slotId, kind, anchor };
}

/**
 * Places the current guided building on the recommended guide anchor.
 * Starter Ride, Snack Kiosk, and Drink Kiosk only. No world-pick argument.
 */
export function confirmGuidedPlacement(session: GuidedConfirmSession, requestedKind?: EntityKind): GuidedConfirmResult {
  const plan = guidedConfirmPlan(session.state, requestedKind);
  if (!plan.ok) {
    return plan;
  }
  return session.place(plan.slotId, plan.kind, { anchor: plan.anchor, rotation: 0 });
}

const COLLECT_ALL_HIDDEN_STEPS = new Set<OnboardingStep>(['build_ride', 'build_snack', 'collect_drink']);

/**
 * Collect All is hidden for the manual collect lesson and the two builds before it.
 * An open sheet hides it so the control does not cover that sheet.
 */
export function collectAllControlVisible(step: OnboardingStep | undefined, panelOpen = false): boolean {
  if (panelOpen || !step || COLLECT_ALL_HIDDEN_STEPS.has(step)) {
    return false;
  }
  return step === 'build_drink' || step === 'complete';
}

/** True only during the three guided build steps, with a real anchor, and no open sheet covering it. */
export function guidedConfirmControlVisible(state: ParkState, panelOpen = false): boolean {
  if (panelOpen) {
    return false;
  }
  const kind = guidedStepKind(state);
  if (!kind) {
    return false;
  }
  return recommendedGuideAnchor(state, kind) !== null && recommendedGuideSlotId(state, kind) !== null;
}

function ensureHudButton(id: string): HTMLButtonElement {
  const existing = document.getElementById(id);
  if (existing) {
    return existing as HTMLButtonElement;
  }
  const button = document.createElement('button');
  button.type = 'button';
  button.id = id;
  button.hidden = true;
  const root = document.getElementById('hud');
  if (root) {
    root.appendChild(button);
  }
  return button;
}

function ensureGuidedConfirmButton(): HTMLButtonElement {
  return ensureHudButton('btn-guided-confirm');
}

function ensureCollectAllButton(): HTMLButtonElement {
  return ensureHudButton('btn-collect-all');
}

export interface Hud {
  refresh(state: ParkState, onboardingStep?: OnboardingStep): void;
  refreshEntityIncome(entity: PlacedEntity): void;
  suppressTransientPointer(durationMs?: number): void;
  setBuildMode(active: boolean): void;
  setBuildGuide(active: boolean): void;
  setFloorMode(active: boolean): void;
  setLandMode(active: boolean): void;
  floorStyle(): FloorStyleId;
  setSoundMuted(muted: boolean): void;
  toast(message: string): void;
  hidePanel(): void;
  showBuildChoices(
    slotId: string,
    state: ParkState,
    onChoose: (kind: EntityKind) => void,
    emphasizeKind?: EntityKind,
    canvas?: boolean,
    rewarded?: RewardedCtaOptions & { couponActive?: boolean },
  ): void;
  showEntity(
    entity: PlacedEntity,
    state: ParkState,
    handlers: EntityPanelHandlers,
    emphasize?: { collect?: boolean; upgrade?: boolean },
  ): void;
  showExpand(
    state: ParkState,
    parcelId: string,
    onBuy: () => void,
    options?: { recommendedPlotIds?: readonly string[]; rewarded?: RewardedCtaOptions; effectiveCost?: number },
  ): void;
  showPlacementDraft(title: string, handlers: { onRotate: () => void; onCancel: () => void }): void;
  showBoosts(options: { autoCollectActive: boolean; remainingLabel?: string; rewarded: RewardedCtaOptions }): void;
  showWelcomeBack(amount: number, options: { onContinue: () => void; rewarded: RewardedCtaOptions }): void;
  setBoostVisible(visible: boolean): void;
  setAdBreakActive(active: boolean): void;
  setPanelTarget(target: PanelPoint | null): void;
  allowsScenePointer(nowMs?: number): boolean;
}

function mustEl<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) {
    throw new Error(`Missing #${id}`);
  }
  return node as T;
}

export function failMessage(reason: string): string {
  const key = `fail.${reason}`;
  const localized = t(key);
  return localized === key ? t('fail.generic') : localized;
}

export function kioskCapacityNote(state: ParkState): string {
  const counter = t('note.kioskCap', {
    used: kioskCount(state),
    capacity: kioskCapacity(state),
  });
  const kind = kioskCapUiKind(state);
  if (kind === 'first') {
    return `${counter} · ${t('note.kioskCapFirst')}`;
  }
  if (kind === 'grow') {
    return `${counter} · ${t('note.kioskCapGrow')}`;
  }
  if (kind === 'final') {
    return `${counter} · ${t('note.kioskCapFinal')}`;
  }
  return counter;
}

/** Existing expansion-panel note: gate reason, coin progress, or Ready. */
export function expandPanelNote(state: ParkState, parcelId: string): string {
  if (starterGateActive(state)) {
    return t('note.starterGate');
  }
  const gate = evaluateParcelUnlock(parcelId, state);
  if (!gate.ok) {
    return gate.reason;
  }
  const cost = expansionCost(parcelId);
  if (state.coins < cost) {
    return t('goal.coins', { have: state.coins, cost });
  }
  return t('goal.ready', { cost });
}

/** Two representative colours for a palette preset, used by the Colors swatch buttons. */
export function paletteSwatchColors(paletteId: string): [string, string] | null {
  const preset = isCarouselPaletteId(paletteId)
    ? CAROUSEL_PALETTES[paletteId]
    : isCoasterPaletteId(paletteId)
      ? COASTER_PALETTES[paletteId]
      : null;
  if (!preset) {
    return null;
  }
  const hex = (value: number) => `#${value.toString(16).padStart(6, '0')}`;
  return [hex(preset.palette.primary), hex(preset.palette.accent)];
}

const FLOOR_LABEL_KEYS: Record<FloorStyleId, string> = {
  classic: 'hud.classic',
  brick: 'hud.brick',
  stone: 'hud.stone',
  grass: 'hud.erase',
  kidsTile: 'hud.kidsTile',
  wood: 'hud.wood',
  festive: 'hud.festive',
  flowerPink: 'hud.flowerPink',
  flowerYellow: 'hud.flowerYellow',
  flowerMix: 'hud.flowerMix',
  clover: 'hud.clover',
  garden: 'hud.garden',
};

function isFloorStyleId(value: string | undefined): value is FloorStyleId {
  return value === 'grass' || (FLOOR_STYLE_IDS as readonly string[]).includes(value ?? '');
}

interface ButtonFace {
  icon: HTMLElement;
  label: HTMLElement;
  iconId: HudIconId | null;
}

/**
 * Icon-first button face: `<span.hud-icon>` (inline SVG, aria-hidden) + `<span.hud-label>`.
 * The label keeps the localized action text so `textContent`, screen readers and tooltips still
 * read the action; CSS decides whether the label is visible or icon-only.
 */
function createButtonFace(button: HTMLElement): ButtonFace {
  button.textContent = '';
  const icon = document.createElement('span');
  icon.className = 'hud-icon';
  icon.setAttribute?.('aria-hidden', 'true');
  const label = document.createElement('span');
  label.className = 'hud-label';
  button.appendChild(icon);
  button.appendChild(label);
  return { icon, label, iconId: null };
}

function paintFace(face: ButtonFace, button: HTMLElement, icon: HudIconId | null, label: string): void {
  if (face.iconId !== icon) {
    face.icon.innerHTML = icon ? hudIconMarkup(icon) : '';
    face.iconId = icon;
  }
  face.label.textContent = label;
  if (typeof button.setAttribute === 'function') {
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
  }
}

export function createHud(options: {
  onToggleBuild: () => void;
  onReset: () => void;
  onToggleFloor?: () => void;
  onToggleLand?: () => void;
  onToggleSound?: () => void;
  onLocaleChange?: () => void;
  onClosePanel?: () => void;
  onFindLand?: (kind: EntityKind) => void;
  onRotateView?: (delta: -1 | 1) => void;
  onZoomView?: (delta: -1 | 1) => void;
  onToggleBoost?: () => void;
  onGuidedConfirm?: () => void;
  onCollectAll?: () => void;
}): Hud {
  initLocale();
  const coinsEl = mustEl<HTMLDivElement>('coins');
  const scoreEl = mustEl<HTMLDivElement>('score');
  const goalEl = mustEl<HTMLDivElement>('goal');
  const buildBtn = mustEl<HTMLButtonElement>('btn-build');
  const landBtn = mustEl<HTMLButtonElement>('btn-land');
  const floorBtn = mustEl<HTMLButtonElement>('btn-floor');
  const floorTools = mustEl<HTMLDivElement>('floor-tools');
  const soundBtn = mustEl<HTMLButtonElement>('btn-sound');
  const langBtn = mustEl<HTMLButtonElement>('btn-lang');
  const resetBtn = mustEl<HTMLButtonElement>('btn-reset');
  const toastEl = mustEl<HTMLDivElement>('toast');
  const panel = mustEl<HTMLElement>('panel');
  const panelTitle = mustEl<HTMLDivElement>('panel-title');
  const panelBody = mustEl<HTMLDivElement>('panel-body');
  const panelClose = mustEl<HTMLButtonElement>('panel-close');
  const panelCollapse = document.getElementById('panel-collapse') as HTMLButtonElement | null;
  const viewLeftBtn = document.getElementById('btn-view-left') as HTMLButtonElement | null;
  const viewRightBtn = document.getElementById('btn-view-right') as HTMLButtonElement | null;
  const zoomInBtn = document.getElementById('btn-zoom-in') as HTMLButtonElement | null;
  const zoomOutBtn = document.getElementById('btn-zoom-out') as HTMLButtonElement | null;
  const boostBtn = document.getElementById('btn-boost') as HTMLButtonElement | null;
  const adBreakOverlay = document.getElementById('ad-break-overlay') as HTMLElement | null;
  const confirmBtn = ensureGuidedConfirmButton();
  const collectAllBtn = ensureCollectAllButton();
  const adBreakMessage = document.getElementById('ad-break-message') as HTMLElement | null;

  const faces = new WeakMap<HTMLElement, ButtonFace>();
  function setFace(button: HTMLElement, icon: HudIconId | null, label: string): void {
    let face = faces.get(button);
    if (!face) {
      face = createButtonFace(button);
      faces.set(button, face);
    }
    paintFace(face, button, icon, label);
  }

  let toastTimer = 0;
  let suppressUntilMs = 0;
  let guardTimer = 0;
  let selectedFloorStyle: FloorStyleId = 'brick';
  let lastState: ParkState | null = null;
  let lastOnboardingStep: OnboardingStep | undefined;
  let soundMuted = false;
  let buildActive = false;
  let floorActive = false;
  let landActive = false;
  let adBreakActive = false;
  let boostVisible = false;
  let panelTarget: PanelPoint | null = null;
  let panelCollapsed = false;
  let panelDragPos: PanelPoint | null = null;
  let panelDragBase: { left: number; top: number; width: number; height: number } | null = null;
  let panelDragPointer: number | null = null;
  let panelDragStart: PanelPoint | null = null;

  function resetPanelDrag(): void {
    panelDragPos = null;
    panelDragBase = null;
    panelDragPointer = null;
    panelDragStart = null;
    panel.classList.remove('is-dragging');
  }

  function readHudPanelViewport(): { width: number; height: number } {
    const visual = window.visualViewport;
    return {
      width: visual && visual.width > 0 ? visual.width : window.innerWidth || 1280,
      height: visual && visual.height > 0 ? visual.height : window.innerHeight || 720,
    };
  }

  function applyPanelPlacement(): void {
    if (panel.hidden) {
      return;
    }
    panel.classList.toggle('is-collapsed', panelCollapsed);
    const viewport = readHudPanelViewport();
    let width = Math.min(440, viewport.width - 24);
    let height = panelCollapsed ? 56 : 200;
    // Lift the previous clamp first. Measuring while maxHeight is still the
    // empty-chrome size (openPanel clears the body) would lock a title-only box
    // and clip [data-role="land-meta"] / [data-action="buy-land"].
    if (panel.style) {
      panel.style.maxHeight = '';
    }
    if (typeof panel.getBoundingClientRect === 'function') {
      const rect = panel.getBoundingClientRect();
      if (rect.width > 1) {
        width = rect.width;
      }
      if (rect.height > 1) {
        height = panelCollapsed ? Math.min(rect.height, 56) : Math.max(rect.height, 200);
      }
    }
    if (!panelCollapsed) {
      const scrollHeight = (panel as HTMLElement).scrollHeight;
      if (typeof scrollHeight === 'number' && scrollHeight > 1) {
        height = Math.max(height, scrollHeight);
      }
    }
    const placement = placeHudPanel({
      viewport,
      target: panelDragPos ? null : panelTarget,
      panel: { width, height },
    });
    const headerDrag = placement.mode === 'sheet' || hudPanelDragEnabled(viewport.width);
    if (!headerDrag) {
      resetPanelDrag();
    }
    let left = placement.left;
    let top = placement.top;
    if (panelDragPos && headerDrag) {
      // Measured height includes uncapped scrollHeight. The sheet renders at
      // placement.maxHeight, so re-clamp against that box or a tall dragged
      // panel jumps to the top safe area when visualViewport changes.
      const renderedHeight = Math.min(height, placement.maxHeight);
      const clamped = clampHudPanelDragOffset(
        { left: 0, top: 0, width: placement.width, height: renderedHeight },
        panelDragPos,
        viewport,
      );
      panelDragPos = clamped;
      left = clamped.x;
      top = clamped.y;
    }
    panel.classList.toggle('is-sheet', placement.mode === 'sheet');
    panel.classList.toggle('is-header-draggable', headerDrag);
    if (panel.style) {
      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.right = 'auto';
      panel.style.bottom = 'auto';
      panel.style.width = `${placement.width}px`;
      panel.style.maxHeight = `${placement.maxHeight}px`;
    }
    if (panelCollapse) {
      panelCollapse.hidden = placement.mode !== 'sheet';
      paintCollapse();
    }
  }

  function paintCollapse(): void {
    if (!panelCollapse) {
      return;
    }
    setFace(
      panelCollapse,
      panelCollapsed ? 'chevronUp' : 'chevronDown',
      panelCollapsed ? t('hud.expandPanel') : t('hud.collapse'),
    );
    panelCollapse.setAttribute('aria-expanded', panelCollapsed ? 'false' : 'true');
  }

  function setPanelTarget(target: PanelPoint | null): void {
    panelTarget = target;
    applyPanelPlacement();
  }

  function suppressTransientPointer(durationMs = HUD_GHOST_CLICK_MS): void {
    suppressUntilMs = Date.now() + durationMs;
    panel.classList.add('panel-input-guard');
    window.clearTimeout(guardTimer);
    guardTimer = window.setTimeout(() => {
      panel.classList.remove('panel-input-guard');
    }, durationMs);
  }

  function paintModeButtons(): void {
    setFace(buildBtn, buildActive ? 'check' : 'build', buildActive ? t('hud.done') : t('hud.build'));
    setFace(landBtn, landActive ? 'check' : 'land', landActive ? t('hud.done') : t('hud.land'));
    setFace(floorBtn, floorActive ? 'check' : 'floor', floorActive ? t('hud.done') : t('hud.floor'));
    setFace(soundBtn, soundMuted ? 'muted' : 'sound', soundMuted ? t('hud.muted') : t('hud.sound'));
  }

  function applyChrome(): void {
    const root = document.documentElement;
    if (root) {
      root.lang = getLocale();
    }
    setFace(langBtn, 'globe', localeShort());
    if (typeof langBtn.setAttribute === 'function') {
      langBtn.setAttribute('aria-label', `${t('hud.language')} ${localeShort()}`);
      langBtn.setAttribute('title', t('hud.language'));
    }
    paintModeButtons();
    buildBtn.dataset.action = 'build';
    landBtn.dataset.action = 'land';
    floorBtn.dataset.action = 'floor';
    soundBtn.dataset.action = 'sound';
    if (viewLeftBtn) {
      setFace(viewLeftBtn, 'viewLeft', t('hud.viewLeft'));
      viewLeftBtn.dataset.action = 'view-left';
    }
    if (viewRightBtn) {
      setFace(viewRightBtn, 'viewRight', t('hud.viewRight'));
      viewRightBtn.dataset.action = 'view-right';
    }
    if (zoomInBtn) {
      setFace(zoomInBtn, 'zoomIn', t('hud.zoomIn'));
      zoomInBtn.dataset.action = 'zoom-in';
    }
    if (zoomOutBtn) {
      setFace(zoomOutBtn, 'zoomOut', t('hud.zoomOut'));
      zoomOutBtn.dataset.action = 'zoom-out';
    }
    if (boostBtn) {
      setFace(boostBtn, 'star', t('hud.boost'));
      boostBtn.dataset.action = 'boost';
      boostBtn.hidden = !boostVisible;
    }
    setFace(confirmBtn, 'check', t('hud.placeHere'));
    confirmBtn.dataset.action = 'guided-confirm';
    syncGuidedConfirm();
    setFace(collectAllBtn, 'collect', t('hud.collectAll'));
    collectAllBtn.dataset.action = 'collect-all';
    syncCollectAll();
    if (adBreakMessage) {
      adBreakMessage.textContent = t('rewarded.watching');
    }
    resetBtn.textContent = t('hud.reset');
    setFace(panelClose, 'close', t('hud.close'));
    paintCollapse();
    for (const button of floorTools.querySelectorAll<HTMLButtonElement>('button')) {
      const style = button.dataset.floorStyle;
      if (isFloorStyleId(style)) {
        decorateFloorButton(button, style, t(FLOOR_LABEL_KEYS[style]));
      }
    }
    for (const label of floorTools.querySelectorAll<HTMLElement>('.floor-group-label')) {
      const group = label.dataset.floorGroupLabel;
      if (group === 'paths') label.textContent = t('hud.floorPaths');
      if (group === 'flowers') label.textContent = t('hud.floorFlowers');
    }
    if (lastState) {
      coinsEl.textContent = t('hud.coins', { n: lastState.coins });
      scoreEl.textContent = t('hud.park', { n: Math.round(computeParkScore(lastState)) });
      goalEl.textContent = formatHudGoal(lastState, lastOnboardingStep);
    }
    applyStarterFloorGate();
  }

  applyChrome();

  if (shouldExposeDeveloperReset(import.meta.env.DEV)) {
    resetBtn.hidden = false;
    resetBtn.addEventListener('click', () => options.onReset());
  }

  function guarded(handler: () => void): (event: Event) => void {
    return (event) => {
      if (!hudCommandAllowed(Date.now(), suppressUntilMs)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      handler();
    };
  }

  confirmBtn.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    suppressTransientPointer();
  });
  confirmBtn.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    options.onGuidedConfirm?.();
  });
  collectAllBtn.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    suppressTransientPointer();
  });
  collectAllBtn.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    options.onCollectAll?.();
  });
  buildBtn.addEventListener('click', guarded(() => options.onToggleBuild()));
  landBtn.addEventListener('click', guarded(() => options.onToggleLand?.()));
  floorBtn.addEventListener('click', guarded(() => options.onToggleFloor?.()));
  soundBtn.addEventListener('click', guarded(() => options.onToggleSound?.()));
  viewLeftBtn?.addEventListener('click', guarded(() => options.onRotateView?.(-1)));
  viewRightBtn?.addEventListener('click', guarded(() => options.onRotateView?.(1)));
  zoomInBtn?.addEventListener('click', guarded(() => options.onZoomView?.(1)));
  zoomOutBtn?.addEventListener('click', guarded(() => options.onZoomView?.(-1)));
  boostBtn?.addEventListener('click', guarded(() => options.onToggleBoost?.()));
  langBtn.addEventListener(
    'click',
    guarded(() => {
      cycleLocale();
      applyChrome();
      options.onLocaleChange?.();
    }),
  );
  for (const button of floorTools.querySelectorAll<HTMLButtonElement>('button')) {
    button.addEventListener(
      'click',
      guarded(() => {
        const style = button.dataset.floorStyle;
        if (isFloorStyleId(style)) {
          selectedFloorStyle = style;
          syncFloorStyleButtons();
        }
      }),
    );
  }
  function closeInspect(): void {
    panel.hidden = true;
  }
  panelClose.addEventListener(
    'click',
    guarded(() => {
      closeInspect();
      options.onClosePanel?.();
    }),
  );
  panelCollapse?.addEventListener(
    'click',
    guarded(() => {
      panelCollapsed = !panelCollapsed;
      applyPanelPlacement();
    }),
  );
  if (typeof window.addEventListener === 'function') {
    window.addEventListener('resize', () => applyPanelPlacement());
    window.visualViewport?.addEventListener('resize', () => applyPanelPlacement());
    window.addEventListener('pointermove', (event: PointerEvent) => {
      if (panelDragPointer !== event.pointerId || !panelDragBase || !panelDragStart) {
        return;
      }
      const viewport = readHudPanelViewport();
      const clamped = clampHudPanelDragOffset(
        panelDragBase,
        { x: event.clientX - panelDragStart.x, y: event.clientY - panelDragStart.y },
        viewport,
      );
      panelDragPos = {
        x: panelDragBase.left + clamped.x,
        y: panelDragBase.top + clamped.y,
      };
      if (panel.style) {
        panel.style.left = `${panelDragPos.x}px`;
        panel.style.top = `${panelDragPos.y}px`;
      }
    });
    window.addEventListener('pointerup', (event: PointerEvent) => {
      if (panelDragPointer !== event.pointerId) {
        return;
      }
      if (typeof panelTitle.releasePointerCapture === 'function') {
        try {
          panelTitle.releasePointerCapture(event.pointerId);
        } catch {
          // Capture may already be released.
        }
      }
      panelDragPointer = null;
      panelDragBase = null;
      panelDragStart = null;
      panel.classList.remove('is-dragging');
    });
    window.addEventListener('pointercancel', () => {
      panelDragPointer = null;
      panelDragBase = null;
      panelDragStart = null;
      panel.classList.remove('is-dragging');
    });
  }
  if (typeof panelTitle.addEventListener === 'function') {
    panelTitle.addEventListener('pointerdown', (event: PointerEvent) => {
      if (event.button !== 0 || panel.hidden || !panel.classList.contains('is-header-draggable')) {
        return;
      }
      const rect =
        typeof panel.getBoundingClientRect === 'function'
          ? panel.getBoundingClientRect()
          : { left: 0, top: 0, width: 320, height: 200 };
      panelDragPointer = event.pointerId;
      panelDragStart = { x: event.clientX, y: event.clientY };
      panelDragBase = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
      panel.classList.add('is-dragging');
      if (typeof panelTitle.setPointerCapture === 'function') {
        try {
          panelTitle.setPointerCapture(event.pointerId);
        } catch {
          // Capture is best-effort so the drag does not fall through to the scene.
        }
      }
      event.preventDefault();
      event.stopPropagation();
    });
  }

  function refresh(state: ParkState, onboardingStep?: OnboardingStep): void {
    lastState = state;
    if (onboardingStep !== undefined) {
      lastOnboardingStep = onboardingStep;
    }
    coinsEl.textContent = t('hud.coins', { n: state.coins });
    scoreEl.textContent = t('hud.park', { n: Math.round(computeParkScore(state)) });
    goalEl.textContent = formatHudGoal(state, lastOnboardingStep);
    applyStarterFloorGate();
    syncGuidedConfirm();
    syncCollectAll();
  }

  function syncGuidedConfirm(): void {
    const visible = lastState ? guidedConfirmControlVisible(lastState, !panel.hidden) : false;
    confirmBtn.hidden = !visible;
  }

  function syncCollectAll(): void {
    collectAllBtn.hidden = !collectAllControlVisible(lastOnboardingStep, !panel.hidden);
  }

  function setBuildGuide(active: boolean): void {
    buildBtn.classList.toggle('guide', active);
  }

  function refreshEntityIncome(entity: PlacedEntity): void {
    if (panel.hidden) {
      return;
    }
    const income = entityPanelIncomeView(entity);
    const stored = panelBody.querySelector<HTMLElement>('[data-role="stored"]');
    if (stored) {
      stored.textContent = income.storedText;
    }
    const collect = panelBody.querySelector<HTMLButtonElement>('[data-action="collect"]');
    if (collect) {
      setFace(collect, 'collect', income.collectLabel);
      collect.disabled = !income.collectEnabled;
    }
  }

  function setBuildMode(active: boolean): void {
    buildActive = active;
    buildBtn.classList.toggle('active', active);
    paintModeButtons();
  }

  function setLandMode(active: boolean): void {
    landActive = active;
    landBtn.classList.toggle('active', active);
    paintModeButtons();
  }

  function syncFloorStyleButtons(): void {
    for (const button of floorTools.querySelectorAll<HTMLButtonElement>('button')) {
      const on = button.dataset.floorStyle === selectedFloorStyle;
      button.classList.toggle('active', on);
      button.setAttribute?.('aria-pressed', on ? 'true' : 'false');
    }
  }

  function setSoundMuted(muted: boolean): void {
    soundMuted = muted;
    soundBtn.classList.toggle('muted', muted);
    paintModeButtons();
  }

  function setFloorMode(active: boolean): void {
    floorActive = active;
    floorBtn.classList.toggle('active', active);
    paintModeButtons();
    floorTools.hidden = !active;
    floorTools.parentElement?.classList.toggle('floor-open', active);
    syncFloorStyleButtons();
    applyStarterFloorGate();
  }

  function applyStarterFloorGate(): void {
    const gated = lastState ? starterGateActive(lastState) : false;
    const reason = t('note.starterGate');
    for (const button of floorTools.querySelectorAll<HTMLButtonElement>('button')) {
      const style = button.dataset.floorStyle;
      if (!isFloorStyleId(style) || style === 'grass') {
        button.disabled = false;
        delete button.dataset.reason;
        if (style === 'grass') {
          decorateFloorButton(button, style, t(FLOOR_LABEL_KEYS[style]));
        }
        continue;
      }
      button.disabled = gated;
      if (gated) {
        const label = t(FLOOR_LABEL_KEYS[style]);
        button.setAttribute('title', `${label} · ${reason}`);
        button.setAttribute('aria-label', `${label} · ${reason}`);
        button.dataset.reason = 'starter_gate';
      } else {
        delete button.dataset.reason;
        decorateFloorButton(button, style, t(FLOOR_LABEL_KEYS[style]));
      }
    }
    let note = floorTools.querySelector<HTMLElement>('[data-role="floor-starter-note"]');
    if (gated) {
      if (!note) {
        note = document.createElement('p');
        note.className = 'panel-note';
        note.dataset.role = 'floor-starter-note';
        floorTools.appendChild(note);
      }
      note.textContent = reason;
      note.hidden = false;
    } else if (note) {
      note.hidden = true;
      note.textContent = '';
    }
  }

  function toast(message: string): void {
    toastEl.hidden = false;
    toastEl.textContent = message;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toastEl.hidden = true;
    }, PACING.toastMs);
  }

  function hidePanel(): void {
    panel.hidden = true;
    panelCollapsed = false;
    resetPanelDrag();
    panel.classList.remove('is-collapsed');
    delete panel.dataset.kind;
    panelBody.replaceChildren();
    syncGuidedConfirm();
    syncCollectAll();
  }

  function openPanel(title: string, icon: HudIconId | null = null): void {
    resetPanelDrag();
    panel.hidden = false;
    panelCollapsed = false;
    panel.classList.remove('is-collapsed');
    delete panel.dataset.kind;
    panelTitle.textContent = title;
    if (icon) {
      panel.dataset.icon = icon;
    } else {
      delete panel.dataset.icon;
    }
    const titleIcon = document.getElementById('panel-icon');
    if (titleIcon) {
      titleIcon.innerHTML = icon ? hudIconMarkup(icon) : '';
      titleIcon.hidden = !icon;
    }
    panelBody.replaceChildren();
    suppressTransientPointer();
    applyPanelPlacement();
    syncGuidedConfirm();
    syncCollectAll();
  }

  function decorateFloorButton(button: HTMLButtonElement, style: string, label: string): void {
    let swatch = button.querySelector<HTMLElement>('.floor-swatch');
    if (!swatch) {
      // Drop the static markup fallback text; the swatch + localized label replace it.
      button.textContent = '';
      swatch = document.createElement('span');
      swatch.className = `floor-swatch floor-swatch-${style}`;
      swatch.setAttribute('aria-hidden', 'true');
      if (style === 'grass') {
        swatch.innerHTML = hudIconMarkup('eraser');
      }
      if (typeof button.insertBefore === 'function') {
        button.insertBefore(swatch, button.firstChild);
      } else {
        button.appendChild(swatch);
      }
    }
    let text = button.querySelector<HTMLElement>('.floor-label');
    if (!text) {
      text = document.createElement('span');
      text.className = 'floor-label';
      button.appendChild(text);
    }
    text.textContent = label;
    let cost = button.querySelector<HTMLElement>('.floor-cost');
    if (!cost) {
      cost = document.createElement('span');
      cost.className = 'floor-cost';
      button.appendChild(cost);
    }
    if (style === 'grass') {
      cost.textContent = t('hud.floorFree');
      delete button.dataset.cost;
      button.setAttribute('aria-label', `${label} · ${t('hud.floorFree')}`);
      button.setAttribute('title', `${label} · ${t('hud.floorFree')}`);
      return;
    }
    cost.textContent = t('hud.floorCost', { n: FLOOR_PAINT_COST });
    button.dataset.cost = String(FLOOR_PAINT_COST);
    button.setAttribute('aria-label', `${label} · ${FLOOR_PAINT_COST}`);
    button.setAttribute('title', `${label} · ${FLOOR_PAINT_COST}`);
  }

  function rewardedWatchButton(action: string, rewarded: RewardedCtaOptions): HTMLButtonElement | null {
    if (!rewarded.visible) {
      return null;
    }
    const cooldownMs = rewarded.cooldownMs ?? 0;
    if (cooldownMs > 0) {
      const wait = actionButton(
        'wait',
        t('rewarded.cooldown', { n: Math.ceil(cooldownMs / 1000) }),
        true,
        () => undefined,
      );
      wait.dataset.action = action;
      wait.dataset.reason = 'cooldown';
      wait.classList.add('quiet');
      return wait;
    }
    if (!rewarded.onWatch) {
      return null;
    }
    const watch = actionButton('star', rewarded.status ?? t('action.watchAdBuild'), false, rewarded.onWatch);
    watch.dataset.action = action;
    watch.classList.add('choice-rewarded');
    return watch;
  }

  function actionButton(
    icon: HudIconId | null,
    label: string,
    disabled: boolean,
    onClick: () => void,
    guide = false,
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = guide ? 'choice guide' : 'choice';
    setFace(button, icon, label);
    button.disabled = disabled;
    button.addEventListener('click', guarded(onClick));
    return button;
  }

  /** Catalog card: kind pictogram + name, with a cost/footprint meta row when buildable. */
  function catalogCard(
    kind: EntityKind,
    name: string,
    meta: { cost?: number; footprint?: string; note?: string; badge?: HudIconId },
    disabled: boolean,
    onClick: () => void,
    guide: boolean,
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = guide ? 'choice card guide' : 'choice card';
    const face = createButtonFace(button);
    faces.set(button, face);
    paintFace(face, button, kind, name);
    if (meta.badge) {
      const badge = document.createElement('span');
      badge.className = `card-badge card-badge-${meta.badge}`;
      badge.setAttribute?.('aria-hidden', 'true');
      badge.innerHTML = hudIconMarkup(meta.badge);
      button.appendChild(badge);
    }
    const parts: string[] = [name];
    if (meta.note !== undefined) {
      const note = document.createElement('span');
      note.className = 'card-note';
      note.textContent = meta.note;
      button.appendChild(note);
      parts.push(meta.note);
    }
    if (meta.cost !== undefined || meta.footprint) {
      const row = document.createElement('span');
      row.className = 'card-meta';
      if (meta.cost !== undefined) {
        const cost = document.createElement('span');
        cost.className = 'card-cost';
        const coin = document.createElement('span');
        coin.className = 'hud-icon hud-icon-inline';
        coin.setAttribute?.('aria-hidden', 'true');
        coin.innerHTML = hudIconMarkup('coin');
        cost.appendChild(coin);
        const value = document.createElement('span');
        value.textContent = String(meta.cost);
        cost.appendChild(value);
        row.appendChild(cost);
        parts.push(`${meta.cost}`);
      }
      if (meta.footprint) {
        const size = document.createElement('span');
        size.className = 'card-size';
        size.textContent = meta.footprint;
        row.appendChild(size);
        parts.push(meta.footprint);
      }
      button.appendChild(row);
    }
    button.setAttribute?.('aria-label', parts.join(' · '));
    button.setAttribute?.('title', parts.join(' · '));
    button.disabled = disabled;
    button.addEventListener('click', guarded(onClick));
    return button;
  }

  function showBuildChoices(
    slotId: string,
    state: ParkState,
    onChoose: (kind: EntityKind) => void,
    emphasizeKind?: EntityKind,
    canvas = false,
    rewarded?: RewardedCtaOptions & { couponActive?: boolean },
  ): void {
    openPanel(canvas ? t('title.buildHere') : t('title.build', { slot: slotId.replace('slot_', '') }), 'build');
    panel.dataset.kind = 'build';
    if (rewarded?.couponActive) {
      const note = document.createElement('p');
      note.className = 'panel-note';
      note.dataset.role = 'build-coupon';
      note.textContent = t('rewarded.buildCouponActive');
      panelBody.appendChild(note);
    }
    const watch = rewarded ? rewardedWatchButton('watch-ad-build', {
      ...rewarded,
      status: t('action.watchAdBuild'),
    }) : null;
    if (watch && !rewarded?.couponActive) {
      watch.classList.add('quiet');
      panelBody.appendChild(watch);
    }
    for (const item of BUILD_CATALOG) {
      const baseCost = purchaseCost(item.kind);
      const cost =
        rewarded?.couponActive && catalogItem(item.kind).role === 'attraction'
          ? discountedIntegerCost(baseCost)
          : baseCost;
      const unlock = evaluateCatalogUnlock(item.kind, state);
      const guide = emphasizeKind === item.kind;
      const name = entityName(item.kind);
      if (isKioskKind(item.kind)) {
        const note = kioskCapacityNote(state);
        const blocked = kioskCapUiKind(state) !== 'open';
        const card = catalogCard(
          item.kind,
          name,
          blocked
            ? { note, badge: 'lock' }
            : unlock.ok
              ? { cost, footprint: catalogFootprintCue(item.kind), note }
              : { note: `${note} · ${unlock.reason}`, badge: 'lock' },
          blocked || !unlock.ok || state.coins < cost,
          blocked || !unlock.ok ? () => undefined : () => onChoose(item.kind),
          guide,
        );
        card.dataset.kind = item.kind;
        card.dataset.kioskUsed = String(kioskCount(state));
        card.dataset.kioskCapacity = String(kioskCapacity(state));
        if (blocked) {
          card.classList.add('locked');
          card.dataset.reason = 'kiosk_capacity';
        } else if (!unlock.ok) {
          card.classList.add('locked');
        } else {
          card.dataset.footprint = catalogFootprintCue(item.kind);
        }
        panelBody.appendChild(card);
        continue;
      }
      if (starterGateActive(state) && item.kind !== 'starter_ride') {
        const locked = catalogCard(
          item.kind,
          name,
          { note: t('note.starterGate'), badge: 'lock' },
          true,
          () => undefined,
          guide,
        );
        locked.classList.add('locked');
        locked.dataset.kind = item.kind;
        locked.dataset.reason = 'starter_gate';
        panelBody.appendChild(locked);
        continue;
      }
      if (!unlock.ok) {
        if (catalogNeedsLandGuidance(item.kind, state) && options.onFindLand) {
          const find = catalogCard(
            item.kind,
            name,
            { note: t('action.findLand'), badge: 'search' },
            false,
            () => options.onFindLand?.(item.kind),
            guide,
          );
          find.dataset.action = 'find-land';
          find.dataset.kind = item.kind;
          find.classList.add('find-land');
          panelBody.appendChild(find);
          continue;
        }
        const locked = catalogCard(item.kind, name, { note: unlock.reason, badge: 'lock' }, true, () => undefined, guide);
        locked.classList.add('locked');
        locked.dataset.kind = item.kind;
        panelBody.appendChild(locked);
        continue;
      }
      const reserveReason = incomeReserveBlockReason(state, item.kind);
      if (reserveReason) {
        const reserved = catalogCard(
          item.kind,
          name,
          { note: failMessage('income_reserve'), badge: 'wait' },
          true,
          () => undefined,
          guide,
        );
        reserved.classList.add('locked');
        reserved.dataset.kind = item.kind;
        reserved.dataset.reason = 'income_reserve';
        panelBody.appendChild(reserved);
        continue;
      }
      const footprint = catalogFootprintCue(item.kind);
      const discounted = cost !== baseCost;
      const choice = catalogCard(
        item.kind,
        name,
        {
          cost,
          footprint,
          note: discounted ? t('rewarded.discounted', { n: cost }) : undefined,
        },
        state.coins < cost,
        () => onChoose(item.kind),
        guide,
      );
      choice.dataset.kind = item.kind;
      choice.dataset.footprint = footprint;
      if (discounted) {
        choice.dataset.discounted = '1';
      }
      panelBody.appendChild(choice);
    }
    applyPanelPlacement();
  }

  function showEntity(
    entity: PlacedEntity,
    state: ParkState,
    handlers: EntityPanelHandlers,
    emphasize?: { collect?: boolean; upgrade?: boolean },
  ): void {
    openPanel(t('title.entity', { name: entityName(entity.kind), level: entity.level }), entity.kind);
    panel.dataset.kind = 'entity';
    const income = entityPanelIncomeView(entity);
    const actions = appendPanelSection('actions');
    const stored = document.createElement('p');
    stored.className = 'panel-note';
    stored.dataset.role = 'stored';
    stored.textContent = income.storedText;
    actions.appendChild(stored);

    if (income.collectible) {
      const collect = actionButton(
        'collect',
        income.collectLabel,
        !income.collectEnabled,
        handlers.onCollect,
        Boolean(emphasize?.collect),
      );
      collect.dataset.action = 'collect';
      collect.classList.add('primary', 'choice-collect');
      actions.appendChild(collect);
    }

    const cost = upgradeCost(entity.kind, entity.level);
    if (cost !== null) {
      const upgrade = actionButton(
        'upgrade',
        t('action.upgrade', { n: cost }),
        state.coins < cost,
        handlers.onUpgrade,
        Boolean(emphasize?.upgrade),
      );
      upgrade.dataset.action = 'upgrade';
      upgrade.classList.add('primary', 'choice-upgrade');
      actions.appendChild(upgrade);
    } else if (catalogItem(entity.kind).role === 'attraction') {
      const note = document.createElement('p');
      note.className = 'panel-note';
      note.textContent = t('note.maxLevel');
      actions.appendChild(note);
    }

    if (handlers.onMove || handlers.onRotate) {
      const place = appendPanelSection('place', t('section.place'));
      if (handlers.relocating) {
        const note = document.createElement('p');
        note.className = 'panel-note';
        note.textContent = t('note.dragPlace');
        place.appendChild(note);
        if (handlers.onRotate) {
          const rotate = actionButton('rotate', t('action.rotate'), false, handlers.onRotate);
          rotate.dataset.action = 'rotate';
          rotate.classList.add('tool');
          place.appendChild(rotate);
        }
        if (handlers.onCancelMove) {
          const cancel = actionButton('cancel', t('action.cancelMove'), false, handlers.onCancelMove);
          cancel.dataset.action = 'cancel-move';
          cancel.classList.add('tool', 'quiet');
          place.appendChild(cancel);
        }
      } else {
        if (handlers.onMove) {
          const move = actionButton('move', t('action.move'), false, handlers.onMove);
          move.dataset.action = 'move';
          move.classList.add('tool');
          place.appendChild(move);
        }
        if (handlers.onRotate) {
          const rotate = actionButton('rotate', t('action.rotate'), false, handlers.onRotate);
          rotate.dataset.action = 'rotate';
          rotate.classList.add('tool');
          place.appendChild(rotate);
        }
      }
    }

    if (handlers.palettes && handlers.palettes.length > 0 && handlers.onPalette) {
      const colors = appendPanelSection('colors', t('section.colors'));
      for (const preset of handlers.palettes) {
        const button = actionButton(null, paletteLabel(preset.id, preset.label), false, () => handlers.onPalette?.(preset.id));
        button.dataset.action = 'palette';
        button.dataset.paletteId = preset.id;
        button.classList.add('swatch');
        button.classList.toggle('active', handlers.selectedPaletteId === preset.id);
        const colorsFor = paletteSwatchColors(preset.id);
        if (colorsFor && button.style) {
          button.style.setProperty?.('--swatch-a', colorsFor[0]);
          button.style.setProperty?.('--swatch-b', colorsFor[1]);
        }
        colors.appendChild(button);
      }
    }

    if (handlers.onRequestRemove) {
      const removeSection = appendPanelSection('remove');
      if (handlers.removeRefund !== undefined) {
        const refund = document.createElement('p');
        refund.className = 'panel-note';
        refund.textContent = t('note.removeRefund', { n: handlers.removeRefund });
        removeSection.appendChild(refund);
      }
      if (handlers.pendingRemove) {
        const confirm = actionButton('check', t('action.confirmRemove'), false, () => handlers.onConfirmRemove?.());
        confirm.dataset.action = 'confirm-remove';
        confirm.classList.add('danger');
        const cancel = actionButton('cancel', t('action.cancel'), false, () => handlers.onCancelRemove?.());
        cancel.dataset.action = 'cancel-remove';
        cancel.classList.add('quiet');
        removeSection.appendChild(confirm);
        removeSection.appendChild(cancel);
      } else {
        const remove = actionButton('remove', t('action.remove'), false, () => handlers.onRequestRemove?.());
        remove.dataset.action = 'remove';
        remove.classList.add('tool', 'quiet');
        removeSection.appendChild(remove);
      }
    }
    applyPanelPlacement();
  }

  function appendPanelSection(kind: string, label?: string): HTMLDivElement {
    const section = document.createElement('div');
    section.className = `panel-section panel-section-${kind}`;
    section.dataset.role = `section-${kind}`;
    if (label) {
      const heading = document.createElement('p');
      heading.className = 'panel-section-label';
      heading.textContent = label;
      section.appendChild(heading);
    }
    panelBody.appendChild(section);
    return section;
  }

  function showPlacementDraft(title: string, handlers: { onRotate: () => void; onCancel: () => void }): void {
    openPanel(title, 'move');
    panel.dataset.kind = 'place';
    const place = appendPanelSection('place', t('section.place'));
    const note = document.createElement('p');
    note.className = 'panel-note';
    note.textContent = t('note.dragPlace');
    place.appendChild(note);
    const rotate = actionButton('rotate', t('action.rotate'), false, handlers.onRotate);
    rotate.dataset.action = 'rotate';
    rotate.classList.add('tool');
    const cancel = actionButton('cancel', t('action.cancel'), false, handlers.onCancel);
    cancel.dataset.action = 'cancel-place';
    cancel.classList.add('tool', 'quiet');
    place.appendChild(rotate);
    place.appendChild(cancel);
    applyPanelPlacement();
  }

  function showExpand(
    state: ParkState,
    parcelId: string,
    onBuy: () => void,
    expandOptions?: { recommendedPlotIds?: readonly string[]; rewarded?: RewardedCtaOptions; effectiveCost?: number },
  ): void {
    const baseCost = expansionCost(parcelId);
    const cost = expandOptions?.effectiveCost ?? baseCost;
    openPanel(parcelName(parcelId) || t('title.lockedLand'), 'land');
    panel.dataset.kind = 'land';
    const gate = evaluateParcelUnlock(parcelId, state);
    const alreadyOwned = !gate.ok && gate.reason === 'Already unlocked';
    const gated = starterGateActive(state);
    const meta = plotPurchaseGuidance(state, parcelId, expandOptions?.recommendedPlotIds ?? []);
    const lines = [t('land.coins', { n: cost }), expandPanelNote(state, parcelId)];
    if (meta.milestone) {
      lines.push(
        t(meta.milestone.kind === 'east' ? 'land.eastMilestone' : 'land.northMilestone', {
          have: meta.milestone.have,
          need: meta.milestone.need,
        }),
      );
    }
    if (meta.advancesKind === 'ferris_wheel') {
      lines.push(t('land.advancesFerris'));
    } else if (meta.advancesKind === 'roller_coaster') {
      lines.push(t('land.advancesCoaster'));
    }
    if (meta.recommended) {
      lines.push(t('land.recommended'));
    }
    const note = document.createElement('p');
    note.className = 'panel-note';
    note.dataset.role = 'land-meta';
    note.textContent = lines.join(' · ');
    panelBody.appendChild(note);
    if (!alreadyOwned) {
      const buy = actionButton(
        'flag',
        t('action.buyLand', { n: cost }),
        gated || !gate.ok || state.coins < cost,
        onBuy,
      );
      buy.dataset.action = 'buy-land';
      buy.classList.add('primary');
      if (gated) {
        buy.dataset.reason = 'starter_gate';
      }
      buy.setAttribute('aria-label', t('action.buyLand', { n: cost }));
      if (cost !== baseCost) {
        buy.dataset.discounted = '1';
      }
      panelBody.appendChild(buy);
    }
    if (cost !== baseCost) {
      const discountNote = document.createElement('p');
      discountNote.className = 'panel-note';
      discountNote.dataset.role = 'land-discount';
      discountNote.textContent = t('rewarded.landVoucherActive');
      panelBody.appendChild(discountNote);
    } else if (expandOptions?.rewarded) {
      const watch = rewardedWatchButton('watch-ad-land', {
        ...expandOptions.rewarded,
        status: t('action.watchAdLand'),
      });
      if (watch) {
        watch.classList.add('quiet');
        panelBody.appendChild(watch);
      }
    }
    applyPanelPlacement();
  }

  function showBoosts(boostOptions: {
    autoCollectActive: boolean;
    remainingLabel?: string;
    rewarded: RewardedCtaOptions;
  }): void {
    openPanel(t('title.boosts'), 'star');
    panel.dataset.kind = 'boosts';
    const note = document.createElement('p');
    note.className = 'panel-note';
    note.dataset.role = 'auto-collect';
    note.textContent = boostOptions.autoCollectActive
      ? (boostOptions.remainingLabel ?? t('rewarded.autoCollectActive', { n: '' }))
      : t('rewarded.autoCollectReady');
    panelBody.appendChild(note);
    if (!boostOptions.autoCollectActive) {
      const watch = rewardedWatchButton('watch-ad-auto-collect', {
        ...boostOptions.rewarded,
        status: t('action.watchAdAutoCollect'),
      });
      if (watch) {
        watch.classList.add('primary');
        panelBody.appendChild(watch);
      }
    }
    applyPanelPlacement();
  }

  function showWelcomeBack(
    amount: number,
    welcome: { onContinue: () => void; rewarded: RewardedCtaOptions },
  ): void {
    openPanel(t('title.welcomeBack'), 'coin');
    panel.dataset.kind = 'welcome-back';
    const note = document.createElement('p');
    note.className = 'panel-note';
    note.dataset.role = 'welcome-back';
    note.textContent = t('rewarded.welcomeBack', { n: amount });
    panelBody.appendChild(note);
    const continueBtn = actionButton('check', t('action.continue'), false, welcome.onContinue);
    continueBtn.dataset.action = 'offline-continue';
    continueBtn.classList.add('primary');
    panelBody.appendChild(continueBtn);
    const watch = rewardedWatchButton('watch-ad-offline', {
      ...welcome.rewarded,
      status: t('action.watchAdOffline2x'),
    });
    if (watch) {
      watch.classList.add('primary');
      panelBody.appendChild(watch);
    }
    applyPanelPlacement();
  }

  function setBoostVisible(visible: boolean): void {
    boostVisible = visible;
    if (boostBtn) {
      boostBtn.hidden = !visible;
    }
  }

  function setAdBreakActive(active: boolean): void {
    adBreakActive = active;
    if (adBreakOverlay) {
      adBreakOverlay.hidden = !active;
    }
    if (adBreakMessage) {
      adBreakMessage.textContent = t('rewarded.watching');
    }
  }

  return {
    refresh,
    refreshEntityIncome,
    suppressTransientPointer,
    setBuildMode,
    setBuildGuide,
    setFloorMode,
    setLandMode,
    floorStyle: () => selectedFloorStyle,
    setSoundMuted,
    toast,
    hidePanel,
    showBuildChoices,
    showEntity,
    showExpand,
    showPlacementDraft,
    showBoosts,
    showWelcomeBack,
    setBoostVisible,
    setAdBreakActive,
    setPanelTarget,
    allowsScenePointer: (nowMs = Date.now()) => !adBreakActive && hudCommandAllowed(nowMs, suppressUntilMs),
  };
}
